/**
 * Sentry Noise Filter — deterministic allowlist of known third-party / non-actionable
 * error signatures.
 *
 * This is the SAFETY NET that runs inside `beforeSend`, alongside Sentry's official
 * `thirdPartyErrorFilterIntegration`. Unlike the integration (which relies on bundle
 * `module_metadata`), this filter is fully deterministic and works even if the metadata
 * is missing — so it always catches the few known offenders.
 *
 * GUIDING PRINCIPLE — fail-safe: only return `true` on a precise, high-confidence match.
 * When in doubt, KEEP the event (return `false`) so real bugs are never hidden.
 *
 * To silence a NEW third-party signature: add a precise string to the arrays below.
 * Never add generic words (e.g. "undefined is not an object", "Failed to fetch") — they
 * would risk swallowing real application errors.
 */

/**
 * Precise substrings that, if present in the error MESSAGE, identify third-party noise.
 * Each entry maps to a documented Sentry issue (see .docs/SENTRY_ISSUES_TRACKING.md).
 */
export const NOISE_MESSAGE_SIGNATURES: readonly string[] = [
  '__firefox__',                        // #9/#10/#11 — Firefox iOS injected content script
  'window.ethereum',                    // #9 — crypto wallet browser extensions (MetaMask, …)
  'selectedAddress',                    // #9 — crypto wallet browser extensions
  'Lock was stolen by another request', // #3 — supabase-js auth lock (navigator.locks), benign
  "evaluating 'window.webkit.messageHandlers'", // #12 — Meta IAB (Instagram/Facebook in-app browser) native bridge
  'Java object is gone',            // #14 — Android WebView addJavascriptInterface bridge torn down (Android sibling of #12)
  'Invalid call to runtime.sendMessage', // #15 — Chrome extension messaging (chrome.runtime.sendMessage), tab already closed
  'Object Not Found Matching Id',   // #16 — Grammarly / similar extension injected-proxy rejection
];

/**
 * Precise filename fragments that, if present in ANY stack frame, identify a third-party
 * script (e.g. the Google Maps CDN). Used for errors whose message is generic but whose
 * stack clearly originates outside our bundle.
 */
export const NOISE_FRAME_SIGNATURES: readonly string[] = [
  'maps-api-v3',         // #1/#5/#6 — Google Maps JS API internals
  'maps.googleapis.com', // #1/#5/#6 — Google Maps JS API CDN
];

/**
 * Full error MESSAGES that, if matched EXACTLY (not as a substring), identify third-party
 * noise. Used for signatures too short/generic to safely substring-match (e.g. minified
 * GTM/gtag internal identifiers like "ga" or "Ba" — matching them via `.includes()` would
 * also catch unrelated real messages containing those letters, e.g. "language").
 * Confirmed via Sentry (07/08/2026): recur across multiple restaurant public pages, always
 * `handled:false` with a single frame-less (`filename: undefined`) stack — never seen with
 * one of our own frames — and the official `thirdPartyErrorFilterIntegration` tags them
 * inconsistently (e.g. "Ba" tagged on some events, not on others), so this exact-match
 * allowlist is the reliable backup for this specific family (#13).
 */
export const NOISE_EXACT_MESSAGE_SIGNATURES: readonly string[] = [
  'ga', // #13 — GTM/gtag internal (Google Analytics container), frame-less
  'Ba', // #13 — same family as "ga", same frame-less pattern
  'Aa', // #13 — same family as "ga"/"Ba", same frame-less pattern
];

/**
 * Returns `true` only when the error is a confidently-identified third-party / benign
 * signature that should NOT be sent to Sentry. Otherwise returns `false` (keep the event).
 *
 * @param message  The error message/value (e.g. `event.exception.values[0].value`).
 * @param frameFilenames  The `filename` of every stack frame in the event (may be empty).
 */
export function isKnownThirdPartyNoise(
  message: string | undefined | null,
  frameFilenames: readonly (string | undefined | null)[] = [],
): boolean {
  if (message) {
    if (NOISE_EXACT_MESSAGE_SIGNATURES.includes(message)) return true;

    for (const signature of NOISE_MESSAGE_SIGNATURES) {
      if (message.includes(signature)) return true;
    }
  }

  for (const filename of frameFilenames) {
    if (!filename) continue;
    for (const signature of NOISE_FRAME_SIGNATURES) {
      if (filename.includes(signature)) return true;
    }
  }

  return false;
}
